import type { PageRef, RouterPage } from '@wix/editor-platform-sdk-types'
import type {
  DynamicPageRouterData,
  VariantRule,
} from '@wix/wix-data-client-common-standalone'
import { keys } from '@wix/wix-data-client-common-standalone'
import { find, findKey, first, get, omit } from 'lodash'
import type { PatternDefinition, RouterConfig } from './routerConfigTypes'
import { generatePageTitle } from './templates'

export const findPageRefByPageRole = (
  router: DynamicPageRouterData,
  pageRole: string,
): PageRef | undefined => {
  const page = router.pages.find(
    ({ pageRoles }) => first(pageRoles) === pageRole,
  )
  return page?.pageRef
}

export const findPageIdByPageRole = (
  router: DynamicPageRouterData,
  pageRole: string,
): string | undefined => {
  const pageRef = findPageRefByPageRole(router, pageRole)
  return pageRef?.id
}

export const getPageRole = (
  routerPages: RouterPage[],
  pageRef: Pick<PageRef, 'id'>,
): string => {
  const routerPage = find(routerPages, page => page.pageRef.id === pageRef.id)!
  const pageRoles = get(routerPage, 'pageRoles')
  return Array.isArray(pageRoles) ? pageRoles[0] : pageRoles
}

export const resolveBasePageRole = ({
  routerConfig,
  pageRole,
}: {
  routerConfig: RouterConfig
  pageRole: string
}): string => {
  const { rules, roleVariations } = routerConfig

  if (!roleVariations || !rules) {
    return pageRole
  }

  const variantRuleId = Object.keys(rules).find(
    ruleId => rules[ruleId]?.pageRole === pageRole,
  )

  if (!variantRuleId) {
    return pageRole
  }

  const basePageRole = Object.keys(roleVariations).find(baseRole =>
    roleVariations[baseRole]?.includes(variantRuleId),
  )

  return basePageRole ?? pageRole
}

export const getVariantRuleForPage = ({
  pageRef,
  routerConfig,
  routerPages,
}: {
  pageRef: Pick<PageRef, 'id'>
  routerConfig: RouterConfig
  routerPages: RouterPage[]
}): { ruleId: string; rule: VariantRule } | undefined => {
  const pageRole = getPageRole(routerPages, pageRef)
  if (!pageRole) {
    return undefined
  }

  const rules = routerConfig.rules ?? {}
  const ruleId = Object.keys(rules).find(
    id => rules[id]?.['pageRole'] === pageRole,
  )
  if (!ruleId) {
    return undefined
  }

  return { ruleId, rule: rules[ruleId]! }
}

export const getPatternDefForPage = (
  routerConfig: RouterConfig,
  routerPages: RouterPage[],
  pageRef: Pick<PageRef, 'id'>,
): { pattern: string; def: PatternDefinition } => {
  const pageRole = resolveBasePageRole({
    routerConfig,
    pageRole: getPageRole(routerPages, pageRef),
  })
  const pattern = findKey(routerConfig.patterns, {
    pageRole,
  }) as string

  return {
    pattern,
    def: routerConfig.patterns[pattern],
  }
}

export const deletePattern = (
  routerConfig: RouterConfig,
  pattern: string,
): RouterConfig => ({
  ...omit(routerConfig, 'patterns'),
  patterns: omit(routerConfig.patterns, pattern),
})

export const addPattern = (
  routerConfig: RouterConfig,
  pattern: string,
  patternDefinition: PatternDefinition,
): RouterConfig => ({
  ...routerConfig,

  patterns: {
    ...routerConfig.patterns,
    [pattern]: patternDefinition,
  },
})

export const updatePagePattern = (
  routerConfig: RouterConfig,
  oldPattern: string,
  newPattern: string,
): RouterConfig => {
  const oldPatternDef = routerConfig.patterns[oldPattern]
  const newPateTitle = generatePageTitle(
    oldPatternDef.config.collection,
    newPattern,
  )
  const newPatternDef = {
    ...oldPatternDef,
    title: newPateTitle,
  }
  return addPattern(
    deletePattern(routerConfig, oldPattern),
    newPattern,
    newPatternDef,
  )
}

export const updatePatternDef = (
  routerConfig: RouterConfig,
  pattern: string,
  newPatternDef: PatternDefinition,
): RouterConfig => ({
  ...routerConfig,

  patterns: {
    ...routerConfig.patterns,
    [pattern]: newPatternDef,
  },
})

export const getAllPatterns = (routerConfig: RouterConfig): string[] =>
  keys(routerConfig.patterns) || []

export const getPatternDef = (
  routerConfig: RouterConfig,
  pattern: string,
): PatternDefinition => routerConfig.patterns[pattern]
